import React, { useState } from "react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useCandidateTranscripts, useTranscript } from "@/hooks/transcripts/useTranscripts";
import { TranscriptsProvider, useTranscriptsUI } from "./TranscriptsProvider";
import { TranscriptList } from "./TranscriptList";
import { TranscriptThread } from "./TranscriptThread";
import { TranscriptContextPanel } from "./TranscriptContextPanel";
import { TranscriptUploadDialog } from "./TranscriptUploadDialog";

export interface EmbeddedTranscriptsProps {
	candidateId: string;
	candidateName?: string;
}

const EmbeddedTranscriptsInner: React.FC<EmbeddedTranscriptsProps> = ({
	candidateId,
	candidateName,
}) => {
	const { selectedId, setSelectedId, contextPanelOpen, setContextPanelOpen } = useTranscriptsUI();
	const listQuery = useCandidateTranscripts(candidateId);
	const { data: selectedTranscript } = useTranscript(selectedId);
	const [uploadOpen, setUploadOpen] = useState(false);

	return (
		<div className="flex h-[70vh] min-h-[420px] flex-col overflow-hidden rounded-sm border border-border bg-background">
			{selectedId ? (
				<div className="h-full animate-slide-in">
					<TranscriptThread
						transcriptId={selectedId}
						onBack={() => setSelectedId(null)}
						contextPanelOpen={contextPanelOpen}
						onToggleContextPanel={() => setContextPanelOpen(!contextPanelOpen)}
						onDeleted={() => setSelectedId(null)}
					/>
					<Sheet open={contextPanelOpen && !!selectedTranscript} onOpenChange={setContextPanelOpen}>
						<SheetContent side="right" className="w-[85vw] max-w-sm p-0">
							<SheetTitle className="sr-only">{selectedTranscript?.subject || ""}</SheetTitle>
							{selectedTranscript && <TranscriptContextPanel transcript={selectedTranscript} />}
						</SheetContent>
					</Sheet>
				</div>
			) : (
				<TranscriptList
					transcripts={listQuery.data}
					isLoading={listQuery.isLoading}
					isError={listQuery.isError}
					isFetching={listQuery.isFetching}
					refetch={listQuery.refetch}
					onUpload={() => setUploadOpen(true)}
					showSync={false}
					showSort={false}
				/>
			)}
			<TranscriptUploadDialog
				open={uploadOpen}
				onOpenChange={setUploadOpen}
				defaultCandidate={candidateName ? { id: candidateId, name: candidateName } : undefined}
				onUploaded={(id) => setSelectedId(id)}
			/>
		</div>
	);
};

/**
 * The transcripts surface embedded in a candidate's detail tab: a candidate-
 * scoped list ⇄ detail stack with the linking panel in a side sheet. The
 * org-wide Teams sync stays on the /transcripts page; uploads made here are
 * pre-linked to the candidate.
 */
export const EmbeddedTranscripts: React.FC<EmbeddedTranscriptsProps> = (props) => (
	<TranscriptsProvider variant="embedded">
		<EmbeddedTranscriptsInner {...props} />
	</TranscriptsProvider>
);
